import {
  CUSTOMER_ATTENTION,
  CUSTOMER_STATUSES,
  type CustomerListRow,
  type CustomerSummary,
} from '@contract';
import { Search01Icon } from '@hugeicons/core-free-icons';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { DataTable } from '@/components/data-table';
import { DateRangeFilter } from '@/components/date-range-filter';
import { ExportButton } from '@/components/export-button';
import { QueryState } from '@/components/feedback/query-state';
import { SkeletonTable } from '@/components/feedback/skeletons';
import { PageHeader } from '@/components/page-header';
import { Icon } from '@/components/primitives/icon';
import { StatusPill } from '@/components/primitives/status-pill';
import {
  OpenRecord,
  PersonCell,
  SummaryBand,
  type SummaryCell,
} from '@/components/queue/queue-parts';
import { useSheetActions } from '@/components/sheet/use-sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { rangeArgs } from '@/lib/date-range';
import { relativeTime } from '@/lib/format';
import { languageName } from '@/lib/language';
import { adminQuery, useKeysetList } from '@/lib/query';
import { STOP_LABEL, STOP_TONE } from '@/lib/registration';

/** `YYYY-MM-DDTHH:mm`, GMT. */
const RANGE_TEXT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

const searchSchema = z.object({
  q: z.string().max(120).optional(),
  status: z.enum(CUSTOMER_STATUSES).optional(),
  attention: z.enum(CUSTOMER_ATTENTION).optional(),
  from: z.string().regex(RANGE_TEXT).optional().catch(undefined),
  to: z.string().regex(RANGE_TEXT).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/customers/')({
  validateSearch: searchSchema,
  component: CustomersPage,
});

const KIND_LABEL: Record<string, string> = {
  phone: 'phone number',
  wallet_number: 'wallet number',
  id: 'id',
  reference: 'provider reference',
  name: 'name',
};

/** A match on one of these is exact, so a single result opens straight away. */
const EXACT_KINDS = new Set(['phone', 'wallet_number', 'id', 'reference']);

function CustomersPage() {
  const { q } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [draft, setDraft] = useState(q ?? '');
  const query = q?.trim() ?? '';

  return (
    <>
      <PageHeader
        title="Customers"
        description="Find a customer by phone, wallet number, name on their ID, or a payment reference."
      />
      <search>
        <form
          className="flex max-w-3xl gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void navigate({ search: { q: draft.trim() || undefined } });
          }}
        >
          <div className="relative flex-1">
            <Icon
              icon={Search01Icon}
              size={16}
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="search"
              aria-label="Search customers"
              className="h-10 pl-9"
              placeholder="Phone, wallet number, name or reference"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
          </div>
          <Button
            type="submit"
            size="lg"
            disabled={draft.trim().length < 3}
            className="h-10"
          >
            Search
          </Button>
        </form>
      </search>
      <p className="-mt-2 text-xs text-muted-foreground">
        Type at least 3 characters. Every search and every look is logged.
      </p>
      {query.length >= 3 ? (
        <Results
          key={query}
          query={query}
          onClear={() => {
            setDraft('');
            void navigate({ search: {} });
          }}
        />
      ) : (
        <Directory />
      )}
    </>
  );
}

const results = createColumnHelper<CustomerSummary>();

const RESULT_COLUMNS = [
  results.display({
    id: 'customer',
    header: 'Customer',
    meta: { sortValue: (row) => row.name ?? '' },
    cell: ({ row }) => <CustomerLink customer={row.original} />,
  }),
  results.display({
    id: 'status',
    header: 'Status',
    cell: ({ row }) => <StatusCell customer={row.original} />,
  }),
  results.accessor('wallets', {
    header: 'Wallets',
    cell: (info) => (
      <span className="block text-right tabular-nums">{info.getValue()}</span>
    ),
  }),
  results.accessor('limit_group', { header: 'Limit group' }),
  results.accessor('language', {
    header: 'Language',
    cell: (info) => languageName(info.getValue()),
  }),
  results.accessor('joined_at', {
    header: 'Joined',
    cell: (info) => relativeTime(info.getValue()),
  }),
];

function Results({
  query,
  onClear,
}: Readonly<{ query: string; onClear: () => void }>) {
  const sheet = useSheetActions();
  const search = useQuery(adminQuery('customers.search', { q: query }));
  const opened = useRef(false);

  // One exact match is the customer: open it, as Enter in the palette does.
  const rows = search.data?.rows;
  const kind = search.data?.kind;
  useEffect(() => {
    const only = rows?.length === 1 ? rows[0] : undefined;
    if (!only || !kind || !EXACT_KINDS.has(kind) || opened.current) return;
    opened.current = true;
    sheet.open({ type: 'cust', id: only.id });
  }, [rows, kind, sheet]);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">
          Results for “{query}”
          {rows ? (
            <span className="font-normal text-muted-foreground">
              {' '}
              · {rows.length === 1 ? '1 customer' : `${rows.length} customers`}
              {kind ? ` by ${KIND_LABEL[kind] ?? kind}` : ''}
            </span>
          ) : null}
        </h2>
        <Button size="sm" variant="ghost" onClick={onClear}>
          Clear
        </Button>
      </div>
      <QueryState
        query={search}
        label="customers"
        skeleton={<SkeletonTable columns={5} rows={4} pager={false} />}
      >
        {(data) => (
          <DataTable
            id="customers-search"
            columns={RESULT_COLUMNS}
            data={data.rows}
            rowLabel={(row) => `Open ${row.name ?? 'customer'}`}
            onRowClick={(row, rowEl) =>
              sheet.open(
                { type: 'cust', id: row.id },
                { fromRowEl: rowEl.querySelector('button') ?? rowEl },
              )
            }
            empty={`No customer matches “${query}”. Check the number, or try the name on their ID.`}
          />
        )}
      </QueryState>
    </section>
  );
}

const directory = createColumnHelper<CustomerListRow>();

const DIRECTORY_COLUMNS = [
  directory.display({
    id: 'customer',
    header: 'Customer',
    meta: { sortValue: (row) => row.name ?? '' },
    cell: ({ row }) => <CustomerLink customer={row.original} />,
  }),
  directory.display({
    id: 'status',
    header: 'Status',
    meta: { sortValue: (row) => row.registration?.code ?? '' },
    cell: ({ row }) => <StatusCell customer={row.original} />,
  }),
  directory.accessor('wallets', {
    header: 'Wallets',
    cell: (info) => (
      <span className="block text-right tabular-nums">{info.getValue()}</span>
    ),
  }),
  directory.display({
    id: 'open',
    header: 'Open items',
    meta: {
      sortValue: (row) => row.open_reports + row.open_cases,
    },
    cell: ({ row }) => <OpenItems row={row.original} />,
  }),
  directory.accessor('limit_group', { header: 'Limit group' }),
  directory.accessor('language', {
    header: 'Language',
    cell: (info) => languageName(info.getValue()),
  }),
  directory.accessor('joined_at', {
    header: 'Joined',
    cell: (info) => relativeTime(info.getValue()),
  }),
];

const STATUS_LABEL: Record<(typeof CUSTOMER_STATUSES)[number], string> = {
  verified: 'Verified',
  unverified: 'Not verified',
  registering: 'Still registering',
  abandoned: 'Abandoned',
  finishing: 'ID passed, not finished',
  archived: 'Archived',
};

const ATTENTION_LABEL: Record<(typeof CUSTOMER_ATTENTION)[number], string> = {
  open_report: 'Has an open report',
  open_case: 'Has an open case',
};

/** Customers newest first, with what each has open. Filters live in the URL. */
function Directory() {
  const sheet = useSheetActions();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const filters = {
    status: search.status,
    attention: search.attention,
    ...rangeArgs(search),
  };
  const list = useKeysetList(
    'customers.list',
    { ...filters, limit: 50 },
    { rows: (data) => data.rows },
  );
  const summary = list.data?.summary;
  const set = (patch: z.infer<typeof searchSchema>) =>
    void navigate({ search: { ...search, ...patch } });
  const cells: SummaryCell[] = [
    {
      label: 'Total',
      value: summary?.total,
      onSelect: () => set({ status: undefined, attention: undefined }),
      active: !search.status && !search.attention,
    },
    {
      label: 'Registered',
      value: summary?.verified,
      onSelect: () => set({ status: 'verified', attention: undefined }),
      active: search.status === 'verified',
    },
    {
      label: 'Still registering',
      value: summary?.registering,
      onSelect: () => set({ status: 'registering', attention: undefined }),
      active: search.status === 'registering',
    },
    {
      label: 'Abandoned',
      value: summary?.abandoned,
      onSelect: () => set({ status: 'abandoned', attention: undefined }),
      active: search.status === 'abandoned',
    },
    {
      label: 'ID passed, not finished',
      value: summary?.finishing,
      onSelect: () => set({ status: 'finishing', attention: undefined }),
      active: search.status === 'finishing',
    },
    {
      label: 'Open reports',
      value: summary?.open_reports,
      tone: 'warning',
      onSelect: () => set({ status: undefined, attention: 'open_report' }),
      active: search.attention === 'open_report',
    },
    {
      label: 'Open cases',
      value: summary?.open_cases,
      tone: 'warning',
      onSelect: () => set({ status: undefined, attention: 'open_case' }),
      active: search.attention === 'open_case',
    },
  ];

  return (
    <>
      <SummaryBand cells={cells} label="Customer figures" />
      <div className="grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap">
        <Select
          label="Status"
          className="w-full sm:w-48"
          value={search.status ?? ''}
          onChange={(event) =>
            set({
              status: (event.target.value || undefined) as
                | (typeof CUSTOMER_STATUSES)[number]
                | undefined,
            })
          }
        >
          <option value="">All active</option>
          {CUSTOMER_STATUSES.map((status) => (
            <option key={status} value={status}>
              {STATUS_LABEL[status]}
            </option>
          ))}
        </Select>
        <Select
          label="Needs attention"
          className="w-full sm:w-56"
          value={search.attention ?? ''}
          onChange={(event) =>
            set({
              attention: (event.target.value || undefined) as
                | (typeof CUSTOMER_ATTENTION)[number]
                | undefined,
            })
          }
        >
          <option value="">Anyone</option>
          {CUSTOMER_ATTENTION.map((attention) => (
            <option key={attention} value={attention}>
              {ATTENTION_LABEL[attention]}
            </option>
          ))}
        </Select>
        <DateRangeFilter
          noun="Joined"
          value={search}
          onChange={(range) => set(range)}
        />
        {search.status || search.attention ? (
          <Button
            variant="ghost"
            onClick={() => set({ status: undefined, attention: undefined })}
          >
            Clear filters
          </Button>
        ) : null}
        <ExportButton source="customers.list" args={{ ...filters }} />
      </div>
      <QueryState
        query={list.query}
        label="customers"
        staleAfterMs={5 * 60_000}
        skeleton={<SkeletonTable columns={6} />}
      >
        {() => (
          <DataTable
            id="customers"
            columns={DIRECTORY_COLUMNS}
            data={list.rows}
            more={list.more}
            rowLabel={(row) => `Open ${row.name ?? 'customer'}`}
            onRowClick={(row, rowEl) =>
              sheet.open(
                { type: 'cust', id: row.id },
                { fromRowEl: rowEl.querySelector('button') ?? rowEl },
              )
            }
            empty={
              search.status || search.attention || search.from || search.to
                ? 'No customers match these filters.'
                : 'No customers yet.'
            }
          />
        )}
      </QueryState>
    </>
  );
}

function CustomerLink({ customer }: Readonly<{ customer: CustomerSummary }>) {
  return (
    <OpenRecord
      sheetRef={{ type: 'cust', id: customer.id }}
      label={`Open ${customer.name ?? 'customer'}`}
      className="max-w-full hover:underline"
    >
      <PersonCell customer={customer} />
    </OpenRecord>
  );
}

/** Registered (quiet), the stop pill for an unfinished registration, or Archived. */
function StatusCell({
  customer,
}: Readonly<{ customer: CustomerSummary & Partial<CustomerListRow> }>) {
  if (customer.archived_at)
    return <StatusPill tone="neutral" label="Archived" />;
  if (customer.registration) {
    return (
      <StatusPill
        tone={STOP_TONE[customer.registration.tone]}
        label={STOP_LABEL[customer.registration.code]}
      />
    );
  }
  return customer.verified ? (
    <StatusPill tone="success" label="Registered" />
  ) : (
    <StatusPill tone="neutral" label="Not verified" />
  );
}

function OpenItems({ row }: Readonly<{ row: CustomerListRow }>) {
  if (row.open_reports + row.open_cases === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {row.open_reports > 0 ? (
        <StatusPill
          tone="info"
          label={`${row.open_reports} ${row.open_reports === 1 ? 'report' : 'reports'}`}
        />
      ) : null}
      {row.open_cases > 0 ? (
        <StatusPill
          tone="info"
          label={`${row.open_cases} ${row.open_cases === 1 ? 'case' : 'cases'}`}
        />
      ) : null}
    </span>
  );
}
